/**
 * Parent area shell — gated behind the password re-prompt in HomeScreen.
 *
 * Visually distinct from Eva's experience per spec §3.2: cooler/sober
 * background, smaller mascot-free header, more form-shaped layouts.
 * The shell handles its own internal sub-navigation (Words / Dashboard
 * / Settings), and "Lock parent area" returns to the home screen
 * without touching the device session.
 *
 * Per spec §3.2 the parent-mode flag is in-memory only. Reloading the
 * page brings you back to Eva's home screen even with the same cookie.
 *
 * Default landing tab is "Words" per spec §3.3 ("Default landing
 * surface after Parent Login").
 */
import { useState } from "react";

import { Button } from "@/components/Button";
import { useNavigation } from "@/context/NavigationContext";
import { ParentDashboard } from "@/screens/parent/ParentDashboard";
import { ParentSettings } from "@/screens/parent/ParentSettings";
import { WordEditor } from "@/screens/parent/WordEditor";
import "./ParentArea.css";

type ParentTab = "words" | "dashboard" | "settings";

export function ParentArea() {
  const { navigate } = useNavigation();
  const [tab, setTab] = useState<ParentTab>("words");

  return (
    <div className="parent">
      <header className="parent__header">
        <h1 className="parent__title">Parent area</h1>
        <nav className="parent__tabs" aria-label="Parent area sections">
          <button
            type="button"
            className={`parent__tab ${tab === "words" ? "is-active" : ""}`}
            onClick={() => setTab("words")}
            aria-current={tab === "words" ? "page" : undefined}
          >
            Words
          </button>
          <button
            type="button"
            className={`parent__tab ${tab === "dashboard" ? "is-active" : ""}`}
            onClick={() => setTab("dashboard")}
            aria-current={tab === "dashboard" ? "page" : undefined}
          >
            Dashboard
          </button>
          <button
            type="button"
            className={`parent__tab ${tab === "settings" ? "is-active" : ""}`}
            onClick={() => setTab("settings")}
            aria-current={tab === "settings" ? "page" : undefined}
          >
            Settings
          </button>
        </nav>
        <Button
          size="small"
          variant="secondary"
          className="parent__lock-button"
          onClick={() => navigate("home")}
        >
          Lock
        </Button>
      </header>

      <main className="parent__body">
        {tab === "words" && <WordEditor />}
        {tab === "dashboard" && <ParentDashboard />}
        {tab === "settings" && <ParentSettings />}
      </main>
    </div>
  );
}
